import React from 'react';

function VistaPreviaFacturaPDF({ 
    factura, 
    client, 
    paciente, 
    encabezadoPersonalizado, 
    respuestaDian,
    facturador,
    contenedorId = 'factura-pdf-editable'
}) {
    // Constantes
    const ITEMS_POR_PAGINA = 25; // Aumentado para formato vertical
    
    // Funciones auxiliares
    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const date = new Date(fecha);
        const dia = String(date.getDate()).padStart(2, '0');
        const mes = String(date.getMonth() + 1).padStart(2, '0');
        const anio = String(date.getFullYear()).slice(-2);
        return `${dia}/${mes}/${anio}`;
    };

    const obtenerNit = () => {
        if (!paciente) return '';
        return paciente.numero_documento || paciente.numeroDocumento || '';
    };

    const construirDireccionCompleta = () => {
        if (!paciente) return '';
        const partes = [];
        if (paciente.direccion) partes.push(paciente.direccion);
        if (paciente.zona) partes.push(paciente.zona);
        if (paciente.nombre_ciudad_residencia) partes.push(paciente.nombre_ciudad_residencia);
        if (paciente.nombre_departamento_residencia) partes.push(paciente.nombre_departamento_residencia);
        if (paciente.nombre_pais_residencia) partes.push(paciente.nombre_pais_residencia);
        return partes.join(', ') || paciente.direccion || '';
    };

    const mostrarSectorSaludFactura = () => {
        if (!factura) return false;
        const poliza = String(factura.policy_number ?? '').trim();
        const contrato = String(factura.contract_number ?? '').trim();
        return poliza !== '' || contrato !== '';
    };

    const valorPolizaFactura = () => String(factura?.policy_number ?? '').trim();
    const valorContratoFactura = () => String(factura?.contract_number ?? '').trim();

    const getMetodoPagoTexto = (codigo) => {
        const metodosPago = {
            '10': 'Efectivo',
            '42': 'Consignación bancaria',
            '1': 'Instrumento no definido',
            '20': 'Cheque',
            '30': 'Transferencia Crédito',
            '31': 'Transferencia Débito',
            '45': 'Transferencia Crédito Bancario',
            '47': 'Transferencia Débito Bancaria',
            '48': 'Tarjeta Crédito',
            '49': 'Tarjeta Débito',
            'ZZZ': 'Acuerdo mutuo'
        };
        return metodosPago[codigo] || codigo;
    };

    const formatearMoneda = (valor) => {
        return Math.round(parseFloat(valor || 0)).toLocaleString('es-CO');
    };

    const mostrarDescripcion = factura.detalle?.some(item => String(item.descripcion || '').trim() !== '');

    const nombrePaciente = paciente 
        ? `${paciente.primer_nombre || ''} ${paciente.segundo_nombre || ''} ${paciente.primer_apellido || ''} ${paciente.segundo_apellido || ''}`.trim()
        : 'N/A';

    // Calcular totales
    const calcularDescuentoTotal = () => {
        return factura.detalle?.reduce((acc, item) => {
            const precioBase = parseFloat(item.precio_unitario) * parseFloat(item.cantidad);
            return acc + (precioBase * (parseFloat(item.porcentaje) / 100));
        }, 0) || 0;
    };

    const calcularImpuestoTotal = () => {
        return factura.detalle?.reduce((acc, item) => {
            const precioBase = parseFloat(item.precio_unitario) * parseFloat(item.cantidad);
            const descuento = precioBase * (parseFloat(item.porcentaje) / 100);
            const ajuste = parseFloat(item.ajuste) || 0;
            const baseAjustada = (precioBase - descuento) + ajuste;
            return acc + (baseAjustada * (parseFloat(item.impuesto) / 100));
        }, 0) || 0;
    };

    const calcularAjusteTotal = () => {
        return factura.detalle?.reduce((acc, item) => {
            return acc + (parseFloat(item.ajuste) || 0);
        }, 0) || 0;
    };

    // Dividir ítems en páginas
    const paginas = [];
    if (factura.detalle && factura.detalle.length > 0) {
        for (let i = 0; i < factura.detalle.length; i += ITEMS_POR_PAGINA) {
            paginas.push(factura.detalle.slice(i, i + ITEMS_POR_PAGINA));
        }
    }

    // Estilos inline
    const estilosContenedor = {
        fontFamily: 'Arial, sans-serif',
        fontSize: '9px',
        color: '#282828',
        backgroundColor: '#ffffff',
        padding: '15mm',
        maxWidth: '210mm',
        margin: '0 auto'
    };

    const estilosBanner = {
        backgroundColor: '#e9ecef',
        padding: '4px 8px',
        fontWeight: 'bold',
        fontSize: '10px',
        textAlign: 'center',
        border: '1px solid #dee2e6',
        marginBottom: '8px'
    };

    const estilosTabla = {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '8px',
        fontSize: '9px'
    };

    const estilosCelda = {
        border: '1px solid #dee2e6',
        padding: '4px'
    };

    const estilosCeldaEncabezado = {
        ...estilosCelda,
        backgroundColor: '#e9ecef',
        fontWeight: 'bold',
        width: '15%'
    };

    const estilosCeldaDato = {
        ...estilosCelda,
        backgroundColor: '#ffffff'
    };

    const estilosTablaItems = {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '8px',
        fontSize: '9px'
    };

    const estilosTh = {
        border: '1px solid #dee2e6',
        padding: '4px',
        backgroundColor: '#e9ecef',
        fontWeight: 'bold',
        textAlign: 'left',
        fontSize: '9px'
    };

    const estilosTd = {
        border: '1px solid #dee2e6',
        padding: '3px',
        fontSize: '9px'
    };

    const estilosTotales = {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '10px'
    };

    const estilosTotalFinal = {
        borderTop: '2px solid #dee2e6',
        fontWeight: 'bold',
        fontSize: '10px'
    };

    const estilosEncabezadoReducido = {
        backgroundColor: '#e9ecef',
        padding: '4px 8px',
        fontSize: '9px',
        fontWeight: 'bold',
        marginBottom: '8px',
        border: '1px solid #dee2e6'
    };

    const estilosFoliacion = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        fontSize: '8px',
        color: '#666',
        marginTop: '10px',
        paddingTop: '5px',
        borderTop: '1px solid #dee2e6'
    };

    const estilosNotaLegal = {
        flex: '1',
        textAlign: 'left',
        fontSize: '7px',
        lineHeight: '1.3',
        paddingRight: '10px'
    };

    const estilosNumeroPagina = {
        textAlign: 'right',
        fontSize: '8px',
        whiteSpace: 'nowrap'
    };

    // Funciones de renderizado
    const renderEncabezado = (esPrimeraPagina) => {
        if (esPrimeraPagina && encabezadoPersonalizado && encabezadoPersonalizado.estado === 1) {
            return (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px', backgroundColor: '#ffffff' }}>
                    <tbody>
                        <tr>
                            <td style={{ width: '30%', padding: '2px', fontSize: '9px', lineHeight: '1.1', verticalAlign: 'top', wordWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: encabezadoPersonalizado.izquierda }} />
                            <td style={{ width: '40%', padding: '2px', textAlign: 'center', fontSize: '10px', lineHeight: '1.1', verticalAlign: 'top', wordWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: encabezadoPersonalizado.centro }} />
                            <td style={{ width: '30%', padding: '2px', textAlign: 'right', fontSize: '9px', lineHeight: '1.1', verticalAlign: 'top', wordWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: encabezadoPersonalizado.derecha }} />
                        </tr>
                    </tbody>
                </table>
            );
        } else if (!esPrimeraPagina) {
            return (
                <div style={estilosEncabezadoReducido}>
                    Factura: {factura.prefijo}{factura.numero_factura} | Cliente: {nombrePaciente}
                </div>
            );
        }
        return null;
    };

    const renderDatosFactura = () => (
        <>
            <div style={estilosBanner}>
                FACTURA DE VENTA: {factura.prefijo || ''}{factura.prefijo ? ' ' : ''}{factura.numero_factura}
            </div>
            <table style={estilosTabla}>
                <tbody>
                    <tr>
                        <td style={estilosCeldaEncabezado}>FACTURA #</td>
                        <td style={estilosCeldaDato}>{factura.numero_factura}</td>
                        <td style={estilosCeldaEncabezado}>PREFIJO</td>
                        <td style={estilosCeldaDato}>{factura.prefijo || 'N/A'}</td>
                        <td style={estilosCeldaEncabezado}>CREACIÓN</td>
                        <td style={estilosCeldaDato}>{formatearFecha(factura.fecha_emision)}</td>
                    </tr>
                    <tr>
                        <td style={estilosCeldaEncabezado}>FORMA DE PAGO</td>
                        <td colSpan="3" style={estilosCeldaDato}>{getMetodoPagoTexto(factura.forma_pago)}</td>
                        <td style={estilosCeldaEncabezado}>VENCIMIENTO</td>
                        <td style={estilosCeldaDato}>{formatearFecha(factura.fecha_vence)}</td>
                    </tr>
                    <tr>
                        <td style={estilosCeldaEncabezado}>FACTURADOR</td>
                        <td colSpan="5" style={estilosCeldaDato}>{facturador?.name || 'N/A'}</td>
                    </tr>
                </tbody>
            </table>
        </>
    );

    const renderDatosCliente = () => (
        <table style={estilosTabla}>
            <tbody>
                <tr>
                    <td style={estilosCeldaEncabezado}>RAZÓN SOCIAL</td>
                    <td colSpan="3" style={estilosCeldaDato}>{nombrePaciente}</td>
                </tr>
                <tr>
                    <td style={estilosCeldaEncabezado}>NIT</td>
                    <td style={estilosCeldaDato}>{obtenerNit()}</td>
                    <td style={estilosCeldaEncabezado}>TELÉFONO</td>
                    <td style={estilosCeldaDato}>{paciente?.telefono || paciente?.celular || ''}</td>
                </tr>
                <tr>
                    <td style={estilosCeldaEncabezado}>EMAIL</td>
                    <td style={estilosCeldaDato}>{paciente?.email || ''}</td>
                    <td style={estilosCeldaEncabezado}>DIRECCIÓN</td>
                    <td style={estilosCeldaDato}>{construirDireccionCompleta()}</td>
                </tr>
                {mostrarSectorSaludFactura() && (
                    <tr>
                        <td style={estilosCeldaEncabezado}>N° PÓLIZA</td>
                        <td style={estilosCeldaDato}>{valorPolizaFactura()}</td>
                        <td style={estilosCeldaEncabezado}>N° CONTRATO</td>
                        <td style={estilosCeldaDato}>{valorContratoFactura()}</td>
                    </tr>
                )}
                {respuestaDian?.uuid && (
                    <tr>
                        <td style={estilosCeldaEncabezado}>CUFE</td>
                        <td colSpan="3" style={{ ...estilosCeldaDato, fontSize: '8px', wordBreak: 'break-all' }}>{respuestaDian.uuid}</td>
                    </tr>
                )}
            </tbody>
        </table>
    );

    const renderTablaItems = (items, incluirEncabezado) => (
        <table style={estilosTablaItems}>
            {incluirEncabezado && (
                <thead>
                    <tr>
                        <th style={{ ...estilosTh, width: '8%' }}>Código</th>
                        <th style={{ ...estilosTh, width: mostrarDescripcion ? '22%' : '32%' }}>Servicio</th>
                        {mostrarDescripcion && (
                            <th style={{ ...estilosTh, width: '18%' }}>Descripción</th>
                        )}
                        <th style={{ ...estilosTh, width: '6%', textAlign: 'center' }}>Cant.</th>
                        <th style={{ ...estilosTh, width: '12%', textAlign: 'right' }}>Precio Unit.</th>
                        <th style={{ ...estilosTh, width: '7%', textAlign: 'center' }}>Desc.</th>
                        <th style={{ ...estilosTh, width: '10%', textAlign: 'right' }}>Ajuste</th>
                        <th style={{ ...estilosTh, width: '7%', textAlign: 'center' }}>Imp.</th>
                        <th style={{ ...estilosTh, width: '14%', textAlign: 'right' }}>Total</th>
                    </tr>
                </thead>
            )}
            <tbody>
                {items.map((item, index) => (
                    <tr key={index}>
                        <td style={estilosTd}>{item.lista_precio?.codigo || ''}</td>
                        <td style={estilosTd}>{item.lista_precio?.nombre || ''}</td>
                        {mostrarDescripcion && (
                            <td style={estilosTd}>{item.descripcion || ''}</td>
                        )}
                        <td style={{ ...estilosTd, textAlign: 'center' }}>{item.cantidad}</td>
                        <td style={{ ...estilosTd, textAlign: 'right' }}>${formatearMoneda(item.precio_unitario)}</td>
                        <td style={{ ...estilosTd, textAlign: 'center' }}>{item.porcentaje}%</td>
                        <td style={{ ...estilosTd, textAlign: 'right' }}>${formatearMoneda(item.ajuste || 0)}</td>
                        <td style={{ ...estilosTd, textAlign: 'center' }}>{item.impuesto}%</td>
                        <td style={{ ...estilosTd, textAlign: 'right', fontWeight: 'bold' }}>${formatearMoneda(item.total)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderTotales = () => {
        const subtotal = parseFloat(factura.valor_bruto);
        const descuento = calcularDescuentoTotal();
        const ajuste = calcularAjusteTotal();
        const impuesto = calcularImpuestoTotal();
        const total = parseFloat(factura.valor_total);
        
        return (
            <div style={estilosTotales}>
                <table style={{ width: 'auto', borderCollapse: 'collapse', fontSize: '9px' }}>
                    <tbody>
                        <tr>
                            <td style={{ textAlign: 'right', padding: '4px 12px 4px 4px', fontWeight: 'bold' }}>Subtotal:</td>
                            <td style={{ textAlign: 'right', padding: '4px', minWidth: '100px' }}>${formatearMoneda(subtotal)}</td>
                        </tr>
                        <tr>
                            <td style={{ textAlign: 'right', padding: '4px 12px 4px 4px', fontWeight: 'bold', color: '#dc3545' }}>Descuento:</td>
                            <td style={{ textAlign: 'right', padding: '4px', color: '#dc3545' }}>-${formatearMoneda(descuento)}</td>
                        </tr>
                        <tr>
                            <td style={{ textAlign: 'right', padding: '4px 12px 4px 4px', fontWeight: 'bold' }}>Ajuste:</td>
                            <td style={{ textAlign: 'right', padding: '4px' }}>${formatearMoneda(ajuste)}</td>
                        </tr>
                        <tr>
                            <td style={{ textAlign: 'right', padding: '4px 12px 4px 4px', fontWeight: 'bold' }}>Impuesto:</td>
                            <td style={{ textAlign: 'right', padding: '4px' }}>${formatearMoneda(impuesto)}</td>
                        </tr>
                        <tr style={estilosTotalFinal}>
                            <td style={{ textAlign: 'right', padding: '6px 12px 6px 4px', fontWeight: 'bold' }}>Total:</td>
                            <td style={{ textAlign: 'right', padding: '6px', fontWeight: 'bold' }}>${formatearMoneda(total)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const renderInfoAbonoPresupuesto = () => {
        const tieneSaldoAbono = factura?.saldo_abono !== null && factura?.saldo_abono !== undefined;
        if (!factura?.presupuesto_id || !tieneSaldoAbono) return null;

        const numPresupuesto = factura.numero_presupuesto || factura.presupuesto_id;
        const valorTotalPresupuestoRaw =
            factura.presupuesto_valor_total ?? factura.presupuestoValorTotal;
        const tieneValorPresupuesto =
            valorTotalPresupuestoRaw !== null &&
            valorTotalPresupuestoRaw !== undefined &&
            valorTotalPresupuestoRaw !== '';

        return (
            <div style={{
                marginTop: '10px',
                padding: '6px 8px',
                border: '1px solid #bee5eb',
                backgroundColor: '#d1ecf1',
                borderRadius: '3px',
                fontSize: '9px',
                color: '#0c5460',
                fontWeight: 'bold'
            }}>
                Abono al Presupuesto #{numPresupuesto}. Valor Total:{' '}
                {tieneValorPresupuesto ? `$${formatearMoneda(valorTotalPresupuestoRaw)}` : 'N/D'}
                . Saldo Pendiente: ${formatearMoneda(factura.saldo_abono)}.
            </div>
        );
    };

    return (
        <div id={contenedorId} style={estilosContenedor}>
            {paginas.map((itemsPagina, indexPagina) => (
                <div key={indexPagina} className={indexPagina > 0 ? 'page-break' : ''}>
                    {/* Encabezado */}
                    {renderEncabezado(indexPagina === 0)}
                    
                    {/* Datos de factura (solo primera página) */}
                    {indexPagina === 0 && renderDatosFactura()}
                    
                    {/* Datos de cliente (solo primera página) */}
                    {indexPagina === 0 && renderDatosCliente()}
                    
                    {/* Tabla de ítems */}
                    {renderTablaItems(itemsPagina, indexPagina === 0)}
                    
                    {/* Totales (solo última página) */}
                    {indexPagina === paginas.length - 1 && renderTotales()}

                    {/* Info de abono de presupuesto (solo última página y si aplica) */}
                    {indexPagina === paginas.length - 1 && renderInfoAbonoPresupuesto()}

                    {/* Observaciones (solo última página) */}
                    {indexPagina === paginas.length - 1 && factura.observaciones && (
                        <div style={{ marginTop: '10px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '9px', marginBottom: '4px' }}>Observaciones:</div>
                            <div style={{ fontSize: '9px' }}>{factura.observaciones}</div>
                        </div>
                    )}

                    {/* Foliación con nota legal */}
                    <div style={estilosFoliacion}>
                        <div style={estilosNotaLegal}>
                            Este documento es un soporte de los servicios prestados. La factura electrónica legal, autorizada por la DIAN, será enviada al correo: {paciente?.email || 'N/A'}
                        </div>
                        <div style={estilosNumeroPagina}>
                            Página {indexPagina + 1} de {paginas.length}
                        </div>
                    </div>

                    {/* CUFE visible y copiable (solo en primera página) */}
                    {indexPagina === 0 && respuestaDian?.uuid && (
                        <div style={{ 
                            marginTop: '10px', 
                            padding: '8px', 
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '3px'
                        }}>
                            <div style={{ 
                                fontWeight: 'bold', 
                                fontSize: '8px', 
                                marginBottom: '4px',
                                color: '#000000'
                            }}>
                                CUFE (Código Único de Factura Electrónica):
                            </div>
                            <div 
                                data-cufe-text="true"
                                style={{ 
                                    fontSize: '7px', 
                                    wordBreak: 'break-all',
                                    fontFamily: 'monospace',
                                    color: '#000000',
                                    lineHeight: '1.4'
                                }}
                            >
                                {respuestaDian.uuid}
                            </div>
                        </div>
                    )}
                </div>
            ))}

            <style>{`
                @media print {
                    .page-break {
                        page-break-before: always;
                        page-break-after: auto;
                    }
                    
                    .no-break {
                        page-break-inside: avoid;
                    }
                    
                    table {
                        page-break-inside: auto;
                    }
                    
                    tr {
                        page-break-inside: avoid;
                        page-break-after: auto;
                    }
                    
                    thead {
                        display: table-header-group;
                    }
                    
                    tfoot {
                        display: table-footer-group;
                    }
                }
            `}</style>
        </div>
    );
}

export default VistaPreviaFacturaPDF;
